/* Серверный режим экрана «Фотосессия» на движке шаблона (план вехи 3, шаги 2–4 экрана; lane plan shoot §2).
   createShoot(createShootServer) (features/shoot/engine.ts) отдаёт сюда крючки экрана, а каждая вставка SRV спрашивает этот объект:
   вид фотосессии, образы, форматы и чипы — из документа экрана (GET /app/studio/screens/shoot) и формы; цена, готовность, создание,
   хвост и итог — только из сервиса заказа кадра (OrderService привязки EngineFrame). Правила:
   - одна привязка — один кадр ServerStudio (ключ кадра и его сервис заказа): у другого кадра — и у нового сервиса под прежним ключом —
     своё состояние формы (S), свой референс, свой блок «Съёмка», своё зеркало входов и свой хвост BotRun, поэтому выбор и заказ одного
     приложения или владельца не видны и не уходят под другим;
   - S — вид входов сервиса: входы изменились не формой (повтор из «Истории», заявка родной формы, «Изменить») — readShoot → S; правка
     формы — writeShoot → входы (одна точка записи — syncFoot шаблона), только опубликованными ключами и кодированием родной формы
     (shoot-inputs.ts), поэтому заявки обеих форм читаются друг другом (I1, откат);
   - «Вы», близкие и персонажи — BotPeople с данными сервера (bridge/server-state): в заказ — «Вы» только готовый, близкие — только
     сохранённые и доступные (people-bridge), персонажи — только если форма публикует chars;
   - свой референс — BotRef с проверкой сервера (ref-bridge поверх media-bridge: лист согласия, одна очередь); «Съёмка» — BotCam только
     при совпадении словарей с сервером (cam-bridge);
   - движок не пишет в sessionStorage и не зовёт создание сам: заявку, ключ, отправку, опрос и отказы ведёт сервис;
   - цена на кнопке — цена кадра × кадры (или скидка формы); в заказ уходит цена сервиса; если они расходятся у платной работы, создание
     закрыто: «Цена этого выбора пока не подтверждена» (родная форма, D11);
   - итог — кадры заказа через owner-protected маршрут (result-gallery) по секциям ответа; «Скачать все» — архив сервера или zip кадров;
     «Сделать рилс», «Полароид-рилс» — первый кадр в «Рилс» после сверки владельца и заказа (result-handoff, решение Q-S5). */
import { endpoints } from '../../api';
import { uiFormat, uiText } from '../../i18n';
import { circleBalance, forgetCircleBalance, rememberCircleBalance, toolPrice } from '../studio/circle/tool-helpers';
import { tailProgress } from '../studio/circle/order-tail-model';
import { studioCheapOffer, type StudioApp, type StudioForm } from '../studio/studio-api';
import { record, rows, safeMediaUrl, text } from '../studio/studio-content';
import { details, detailsKey, leaseSkins, loadDetails, skinUrl } from '../studio/engine-kit/char-cards';
import { esc, failText, receipt, serverFoot, type BotRunApi, type BotRunRun } from '../studio/engine-kit/bot-run-server';
import { createCamBridge, type CamBlock, type CamBridge } from '../studio/engine-kit/cam-bridge';
import { ENGINES, type EngineBinding, type EngineBound, type EngineScreen } from '../studio/engine-kit/engine-host';
import { isImproveKind } from '../studio/engine-kit/improve-service';
import { createInputsMirror, type InputsMirror } from '../studio/engine-kit/inputs-mirror';
import { createMediaBridge, mediaPolicy, type MediaBridge } from '../studio/engine-kit/media-bridge';
import { uploadFailureText, type Intent, type OrderService } from '../studio/engine-kit/order-service';
import { peopleModule, peopleOf, personRef, selfPicture, selfRef, SILHOUETTE, watchPeople, type PeopleModule, type SelItem } from '../studio/engine-kit/people-bridge';
import { createRefBridge, type RefBridge, type RefFile, type RefOpts } from '../studio/engine-kit/ref-bridge';
import { archive, archiveUrl, createResultGallery, galleryKey, ORDER_IMAGE_LIMITS, sections, type ResultGallery } from '../studio/engine-kit/result-gallery';
import { handoffPhoto } from '../studio/engine-kit/result-handoff';
import { createRunDriver, peekIntent, type RunDriver } from '../studio/engine-kit/run-driver';
import { screenDoc, type ScreenDoc } from '../studio/engine-kit/screen-doc';
import { poorBalance, serverUi, type ServerUi } from '../studio/engine-kit/server-ui';
import { chatClick, chatFoot, doneP, openSettings } from '../studio/engine-kit/tail-done';
import { decode, plural, requiredGap } from './kit/form-model';
import { shootKeys } from './shoot-model';
import { hasLooks, readShoot, shootLists, shootSupported, writeShoot, type EngineLook, type ShootConf, type ShootLists, type ShootView } from './shoot-inputs';

/* ---------- контракт с движком (features/shoot/engine.ts, serve({...})) ---------- */

/** Состояние формы шаблона (S) */
export interface ShootState {
  key: string | null;
  shoot: string | null;
  app: unknown;
  looks: string[];
  people: SelItem[];
  extra: unknown[];
  ar: string;
  text: string;
  open: boolean;
  cheap: boolean;
  menu: string | null;
  sh: { view: string } | null;
}
/** Настройки страницы (BotCore.uiDefaults и init каркаса): то, что читает SRV */
export interface EngineUi {
  buzz(kind?: string): void;
  tip(el: Element | null, text: string): void;
  [key: string]: unknown;
}
interface EngineFoot {
  poor(): boolean;
  poorOpen(): void;
  sync(): void;
}
/** Ряд лиц «Кто в кадре» (BotPeople.attach) */
interface EnginePeople {
  /** me.in — «Вы» отмечены в кадре (отметку можно снять: фотосессия с близким или персонажем) */
  snap(): { me: { in: boolean } };
  paintForm(): void;
  prune(): void;
  close(): void;
  readonly isOpen: boolean;
}
/** Свой референс (BotRef.make) */
interface EngineRef {
  o: { n?: unknown };
  active(): boolean;
  busy(): boolean;
  files(): Array<{ upload_id: string | null; st: string }>;
  order(): Array<{ upload_id: string | null; use?: string }>;
  restore(s: { files: RefFile[] } | null): void;
  say(text: string, el?: Element | null): void;
}
interface EngineCam extends CamBlock {
  reset(k?: string): void;
}
/** Крючки, которые движок отдаёт serve (engine.ts, вставка после состояния монтажа): tail, foot, people, ref, cam, set и gs — геттеры */
export interface ShootEngineHooks {
  S: ShootState;
  LOOKS: EngineLook[];
  THEMES: string[];
  AR: string[][];
  WISH: string[][];
  SHOT_NAMES: string[];
  readonly tail: BotRunApi;
  readonly foot: EngineFoot;
  readonly people: EnginePeople;
  readonly ref: EngineRef;
  readonly cam: EngineCam;
  readonly set: { sync(o?: { force?: boolean }): void; close(): void };
  readonly gs: unknown;
  root(): HTMLElement | null;
  mounted(): boolean;
  ui(): EngineUi;
  ic(name: string): string;
  esc(value: string): string;
  setLimits(o: { max?: number; refShots?: number }): void;
  limits(): { max: number; refShots: number };
  paintPage(): void;
  looksSync(id: string): void;
  railPaint(id?: string): void;
  moSync(): void;
  syncFoot(): void;
  sheetClose(): void;
  lbClose(now?: boolean): void;
  curLooks(): EngineLook[];
  nShots(): number;
  refAct(): boolean;
  oneLook(): boolean;
  title(): string;
  runExit(): void;
}
type Missing = 'load' | 'blocked' | 'off' | 'unavailable' | 'docs' | 'docfail' | '';
/** Снимок входа заказа (r.snap BotRun, runSnap шаблона): из тела заявки, а без неё — из формы; у готового — по секциям итога.
    idx — номер файла итога для кадра k (resPic) */
export interface ShootSnap {
  [key: string]: unknown;
  title: string;
  one: boolean;
  looks: Array<{ id: string; n: string; f: string; shots: Array<{ n: string }> }>;
  me: { src: string | null; url: string; av: null; name: string };
  people: Array<{ act: string }>;
  chars: unknown[];
  ar: string;
  cheap: boolean;
  text: string;
  n: number;
  idx: number[];
}

export interface ShootServer {
  ui<U extends object>(base: U): ServerUi<U>;
  open(o: object): void;
  close(): void;
  tail(create: (o: Record<string, unknown>) => BotRunApi, opts: Record<string, unknown>): BotRunApi;
  wrap<T>(slot: 'cheap', value: T, extra?: unknown): T;
  has(k: 'chars' | 'ratio' | 'shoot' | 'wishes'): boolean;
  conf(): ShootConf;
  refOn(): boolean;
  refOpts(): RefOpts;
  lookImg(look: EngineLook): string;
  meUrl(): string;
  unit(): string;
  badge(look: EngineLook): string;
  improveKind(): string;
  /** Слоты «Вид персонажа» карточки «Ещё настройки»: по одному на персонажа каталога в кадре */
  skinItems(): unknown[];
  price(cheap: boolean): number;
  missing(): Missing | null;
  ready(): string;
  missText(kind: string): string;
  label(): string | null;
  write(): void;
  start(btn: HTMLElement): void;
  steps(): string[][];
  snap(): ShootSnap;
  sentPrice(): number;
  drive(r: BotRunRun): void;
  failText(r: BotRunRun): string | null;
  receipt(r: BotRunRun): string;
  /** Подпись итога (T.doneP): доставка заказа — бот уже прислал, пришлёт или не присылает (tail-done) */
  doneText(r: BotRunRun): string;
  resPic(k: number): string;
  saveAll(btn: HTMLElement): void;
  save(btn: HTMLElement, k: number): void;
  leave(go: () => void): void;
  click(d: DOMStringMap, el: HTMLElement, r: unknown): boolean;
}

/* ---------- общие части ---------- */

interface Core {
  html: { unit(n: number, rest: string): string };
  saveBtn?(btn: HTMLElement, make: () => Promise<{ data: Blob; name: string }>, o: object): void;
  zip?(files: Array<{ name: string; data: Blob }>): Promise<Blob>;
}
const core = () => window.BotCore as unknown as Core;
const TRANSPORT = uploadFailureText(null);
/** Кадров в итоге фотосессии (родной хвост: OrderTail limit 48) */
const RESULT_LIMITS = { ...ORDER_IMAGE_LIMITS, limit: 48 };
/** Этапы шаблона (STEPS bot-shoot.js) и ключ этапа сервера там, где он другой (MessagingOrderSteps: look → проверка фото) */
const STEPS: string[][] = [
  ['check', 'image', 'Проверяем фото', 'Фото проверены'],
  ['shoot', 'sparkle', 'Снимаем образы', 'Образы сняты'],
  ['pack', 'send', 'Готовим файлы', 'Файлы готовы'],
];
const STEP_ALIAS = { look: 'check' };
const FAIL = { modH: 'Фотосессия не прошла проверку', techH: 'Не получилось снять фотосессию', notMade: 'фото не созданы' };
const shotsW = (n: number) => plural(n, uiText('кадр'), uiText('кадра'), uiText('кадров'));
const pad = (n: number) => String(n).padStart(2, '0');
const extOf = (mime: string) => (mime === 'image/png' ? '.png' : mime === 'image/webp' ? '.webp' : '.jpg');
function busyButton<T>(btn: HTMLElement | null, fn: () => Promise<T>): Promise<T | undefined> {
  const busy = window.SteosUI?.busy;
  return btn && busy ? busy(btn, fn) : fn();
}
/** BotPeople для подписи ряда лиц: у записи без фото (демо-люди шаблона до server-state) — пустой список фото */
function safePeople(): PeopleModule | null {
  const m = peopleModule();
  if (!m) return null;
  return { ...m, store: { all: () => m.store.all().map(p => ({ ...p, photos: Array.isArray(p.photos) ? p.photos : [] })) } };
}
const isChar = (x: SelItem) => typeof x.id === 'string' && x.id.startsWith('c:');

/** Состояние одной привязки (кадра ServerStudio) */
interface Frame {
  b: EngineBinding;
  key: string;
  svc: OrderService;
  app: StudioApp;
  mirror: InputsMirror;
  driver: RunDriver;
  media: MediaBridge;
  ref: RefBridge;
  cam: CamBridge;
  gallery: ResultGallery;
  doc: ScreenDoc<Record<string, unknown>>;
  /** Форма и документ, из которых собраны списки (lists) */
  form: StudioForm | null;
  data: Record<string, unknown> | undefined;
  lists: ShootLists | null;
  /** Входы уже перенесены в S (вход экрана) */
  entered: boolean;
  /** Раскладка формы на экране: вид фотосессии и опубликованные части */
  layout: string;
  /** Превью своего референса из входов читается: его ключи и 'ref' остаются во входах */
  refRestoring: boolean;
  /** Словари «Съёмки» спрошены (null — ещё нет ответа) */
  camAsked: boolean;
  camDone: boolean;
  said: string;
  peek: Intent | null;
  formShown: boolean;
  balanceAsk: boolean;
  orderSeen: number | undefined;
  /** Заказ и ревизия, кадры которых уже на итоге */
  doneKey: string;
  doneRun: BotRunRun | null;
  moving: boolean;
}

/* ---------- сервер экрана ---------- */

let instance: { bind(b: EngineBinding): EngineBound } | null = null;

export function createShootServer(h: ShootEngineHooks): ShootServer {
  const { S } = h;
  /* демо-образов, тем и чипов шаблона в серверном режиме нет: списки — только из документа экрана */
  h.LOOKS.splice(0);
  h.THEMES.splice(0);
  h.WISH.splice(0);
  let frame: Frame | null = null;
  let bound: Frame | null = null;
  /** Кадр, к которому относится S (форма шаблона) — объект, а не ключ: ключи ServerStudio повторяются после нового входа в раздел */
  let painted: Frame | null = null;
  /** Кадр, из документа которого сейчас списки шаблона (LOOKS, AR…) */
  let filled: Frame | null = null;
  let tailOpts: { create: (o: Record<string, unknown>) => BotRunApi; opts: Record<string, unknown> } | null = null;
  let inst: BotRunApi | null = null;
  const facade = {} as BotRunApi;

  const cur = () => (bound && bound === frame ? frame : null);
  const live = (f: Frame) => {
    const s = f.svc.get();
    return { s, d: f.svc.derive(s) };
  };
  const onForm = () => h.mounted() && !h.tail.run;
  /** «Вы» отмечены в кадре (ряд «Кто в кадре»); ряда ещё нет — по умолчанию в кадре, как у шаблона (selfOn) */
  const meIn = () => h.people?.snap?.().me.in ?? true;
  const tip = (el: Element | null, text: string) => {
    const at = el?.isConnected ? el : (h.root()?.querySelector('.ap-foot .btn') ?? null);
    if (text && at) h.ui().tip(at, uiText(text));
  };
  const entryOf = (f: Frame) => ({ ...record(f.app.entry), ...(f.b.initialInputs ?? {}) });
  const NEUTRAL = (): ShootConf => ({ title: (cur() ?? frame)?.app.name ?? '', pick: false, def: [], looks: [], max: 4, roles: ['adult', 'kid', 'old', 'pet'], acts: [], own: false });

  /* ---- списки шаблона из документа экрана ---- */
  function fillLists(f: Frame) {
    filled = f;
    const L = f.lists;
    if (!L) return;
    h.LOOKS.splice(0, h.LOOKS.length, ...L.looks.map(look => ({ ...look, th: look.th.slice(), shots: look.shots.map(shot => ({ ...shot })) })));
    h.THEMES.splice(0, h.THEMES.length, ...L.themes);
    h.AR.splice(0, h.AR.length, ...L.formats.map(item => [item[0], uiText(item[1]), uiText(item[2])]));
    h.WISH.splice(0, h.WISH.length, ...L.wishes.map(item => item.slice()));
    if (L.shotNames.length) h.SHOT_NAMES.splice(0, h.SHOT_NAMES.length, ...L.shotNames);
    h.setLimits({ max: L.wishMax, refShots: L.ref.shots });
    if (L.ref.name) h.ref.o.n = L.ref.name;
  }

  /* ---- входы ↔ S ---- */
  const viewOf = (): ShootView => ({ looks: S.looks.slice(), ref: h.refAct(), ratio: S.ar, wishes: S.text, sel: S.people.map(x => ({ ...x })) });
  /** Вид входов → S (вход экрана, повтор, «Изменить», заявка родной формы) */
  function apply(f: Frame, v: ShootView, inputs: Record<string, string>) {
    const L = f.lists!;
    /* виды персонажей из входов (chars[].skin_id) — в skins */
    skins.clear();
    const charsKey = shootKeys(f.svc.get().form ?? null).chars;
    let saved: unknown = [];
    try {
      saved = charsKey && inputs[charsKey] ? JSON.parse(inputs[charsKey]) : [];
    } catch {
      saved = [];
    }
    for (const c of rows(saved)) {
      const id = Number(c.avatar_id),
        skin = Number(c.skin_id);
      if (Number.isSafeInteger(id) && id > 0 && Number.isSafeInteger(skin) && skin > 0) skins.set(id, skin);
    }
    Object.assign(S, { shoot: L.kind, app: null, looks: L.conf.pick ? v.looks.slice() : L.conf.looks.slice(), ar: v.ratio, text: v.wishes, people: v.sel, extra: [] });
    /* свой референс: превью загрузок из входов (решение Q7: не открылось — загрузить заново); без него — снят */
    const ids = f.ref.ids(inputs);
    const have = h.ref.order().map(item => item.upload_id).filter(Boolean);
    if (v.ref && ids.length && L.conf.pick) {
      if (!h.ref.active() || have.join() !== ids.join()) restoreRef(f, inputs);
    } else if (h.ref.files().length) h.ref.restore(null);
    /* «Съёмка» из входов — когда словари сервера уже есть (иначе — по их приходу) */
    if (f.cam.has()) f.cam.restore(inputs.shoot);
  }
  /* ---- «Вид персонажа» (владелец 02.11.2026: вид — везде, где персонаж из библиотеки; как у «Рилса с вами») ---- */
  /** Вид персонажа в кадре: id персонажа → id вида карточки; уходит в chars[].skin_id (картинку вида отдаёт Voice) */
  const skins = new Map<number, number>();
  const cardWait = new Set<number>();
  /** Контракт принимает вид: поле chars публикует свойство skin_id */
  function charsSkins(form: StudioForm | null): boolean {
    const key = shootKeys(form).chars;
    if (!form || !key) return false;
    const props = rows(record(form).screen_fields).find(field => field.key === key)?.properties;
    return Array.isArray(props) ? rows(props).some(prop => prop.key === 'skin_id') : 'skin_id' in record(props);
  }
  function skinItems(): unknown[] {
    const f = cur();
    const form = f?.svc.get().form ?? null;
    if (!f || !charsSkins(form)) return [];
    const ids = [...new Set(S.people.flatMap(x => (typeof x.id === 'string' && /^c:[1-9][0-9]*$/.test(x.id) ? [Number(x.id.slice(2))] : [])))];
    for (const id of [...skins.keys()]) if (!ids.includes(id)) skins.delete(id);
    const cards = ids.flatMap(id => {
      const card = details.get(detailsKey(id));
      if (card === undefined && !cardWait.has(id)) {
        cardWait.add(id);
        void loadDetails(id).finally(() => {
          cardWait.delete(id);
          if (cur() === f) h.set.sync();
        });
      }
      return card ? [card] : [];
    });
    return cards.map(card => {
      const id = card.avatar.id;
      leaseSkins(card, () => {
        if (cur() === f) h.set.sync();
      });
      const base = safeMediaUrl(card.avatar.image_thumb_url || card.avatar.image_url) ?? SILHOUETTE;
      const face = (url: string | null) => `<img src="${h.esc(url || base)}" alt="" draggable="false">`;
      const chosen = skins.get(id);
      return {
        k: `skin:${id}`,
        t: cards.length > 1 ? card.avatar.name : uiText('Вид персонажа'),
        kind: 'faces',
        cur: chosen && card.avatar.skins.some(sk => sk.id === chosen) ? String(chosen) : 'def',
        opts: [['def', uiText('Обычный'), '', face(base)], ...card.avatar.skins.map(sk => [String(sk.id), sk.name, '', face(skinUrl(card, sk.id))])],
        set: (v: string) => {
          const skin = Number(v);
          if (Number.isSafeInteger(skin) && skin > 0) skins.set(id, skin);
          else skins.delete(id);
        },
      };
    });
  }
  function restoreRef(f: Frame, inputs: Record<string, string>) {
    f.refRestoring = true;
    void f.ref.restore(inputs).then(res => {
      if (!res) return;
      f.refRestoring = false;
      if (cur() !== f) return;
      if (res.files.length) h.ref.restore({ files: res.files });
      else h.ref.restore(null);
      if (res.text) h.ref.say(res.text);
      if (h.mounted()) changed(f);
    });
  }
  /** «Съёмка» во входы: блока нет у шаблона-апки и при расхождении словарей (null — ключ снять); словари ещё не пришли — ключ не трогать */
  function shootValue(f: Frame): string | null | undefined {
    const form = f.svc.get().form;
    if (!shootKeys(form).shoot) return undefined;
    if (f.lists?.kind === 'app') return null;
    if (!f.camDone) return undefined;
    return f.cam.order() ?? null;
  }
  /** S → входы: одна точка записи (syncFoot шаблона); сервис заперт — запись не идёт (зеркало) */
  function commit(f: Frame) {
    const form = f.svc.get().form;
    const L = f.lists;
    if (!form || !L || !f.entered || f.form !== form) return;
    /* новый в ряду — без действия (act null у BotPeople): в заказ — без action */
    const people = peopleOf({ me: false, sel: S.people.map(x => ({ ...x, act: x.act ?? '' })), m: safePeople() });
    const me = meIn() ? selfRef() : null;
    const shoot = shootValue(f);
    const lookKey = shootKeys(form).looks;
    f.mirror.commit(prev => {
      const view = viewOf();
      /* превью референса из входов ещё читается: его ключи и 'ref' в образах остаются */
      if (f.refRestoring && lookKey) view.ref = (prev[lookKey] ?? '').includes('"ref"') || /(^|,)ref(,|$)/.test(prev[lookKey] ?? '');
      const chars = charsSkins(form) ? people.chars.map(c => (skins.get(c.avatar_id) ? { ...c, skin_id: skins.get(c.avatar_id)! } : c)) : people.chars;
      const next = writeShoot(form, L, view, prev, { me, persons: people.persons, chars, shoot });
      return f.refRestoring ? next : f.ref.inputsFor(next, h.ref);
    });
  }

  /* ---- сверка формы с сервисом (каждый зафиксированный вид) ---- */
  function layoutOf(f: Frame) {
    const L = f.lists;
    if (!L) return '';
    return [L.kind, L.conf.title, L.conf.pick, L.looks.map(look => look.id).join(), ...(['chars', 'ratio', 'shoot', 'wishes'] as const).map(k => server.has(k)), server.refOn(), server.unit(), server.improveKind()].join('|');
  }
  /** Форма, документ и входы → списки шаблона и S (без отрисовки); true — форму надо нарисовать заново */
  function syncState(f: Frame): boolean {
    const { s } = live(f);
    const form = s.form;
    const data = f.doc.data();
    if (!form || !data) return false;
    let repaint = false;
    /* форма перечитана («Обновить форму», «К настройкам» после смены цены или версии): сервис сбросил входы к началу кадра, а выбор
       человека остаётся в S и уходит в новые входы следующей записью (шаблон: назад к форме с теми же настройками) */
    const reread = f.entered && !!f.form && f.form !== form;
    const external = f.entered && !reread && f.mirror.external(s.inputs);
    if (f.form !== form || f.data !== data || filled !== f || external) {
      const before = f.lists?.kind;
      f.form = form;
      f.data = data;
      f.lists = shootLists(data, form, s.inputs, entryOf(f), f.app.name);
      fillLists(f);
      if (before !== undefined && before !== f.lists.kind) f.entered = false;
    }
    const L = f.lists!;
    if (!f.entered) {
      /* вход экрана: входы → S; у общей без образов во входах — образы по умолчанию (default_look_ids, решение Q-S10) */
      const v = readShoot(form, L, s.inputs);
      if (L.conf.pick && !hasLooks(form, s.inputs)) v.looks = L.conf.def.slice();
      apply(f, v, s.inputs);
      f.entered = true;
      f.mirror.seen(s.inputs);
      repaint = true;
    } else if (reread) {
      const known = new Set(L.looks.map(look => look.id));
      S.looks = L.conf.pick ? S.looks.filter(id => known.has(id)) : L.conf.looks.slice();
      S.shoot = L.kind;
      f.mirror.seen(s.inputs);
      repaint = true;
    } else if (external) {
      apply(f, readShoot(form, L, s.inputs), s.inputs);
      f.mirror.seen(s.inputs);
      repaint = true;
    }
    const layout = layoutOf(f);
    if (layout !== f.layout) {
      f.layout = layout;
      repaint = true;
    }
    return repaint;
  }
  function formSync(f: Frame) {
    const repaint = syncState(f);
    if (!onForm()) return;
    if (repaint) h.paintPage();
    /* ошибка формы (файлы повтора недоступны, форма не загрузилась) — один раз у кнопки */
    const now = f.svc.get();
    const error = now.integrityError ? '' : now.error;
    if (error !== f.said) {
      f.said = error;
      if (error && error !== TRANSPORT) tip(null, error);
    }
    h.syncFoot();
  }
  function changed(f: Frame) {
    if (cur() !== f || !h.mounted()) return;
    formSync(f);
    f.driver.sync();
    f.media.sync();
    balance(f);
  }
  /** Остаток для «Не хватает Circle» — тот же, что у родной кнопки (GET /app/balance, память на минуту) */
  function balance(f: Frame) {
    const orderId = f.svc.derive().orderId;
    if (orderId && orderId !== f.orderSeen) {
      f.orderSeen = orderId;
      forgetCircleBalance();
    }
    const shown = onForm(),
      fresh = shown && !f.formShown;
    f.formShown = shown;
    if (!fresh || f.balanceAsk || circleBalance() !== null || f.svc.get().form?.chargeable === false) return;
    f.balanceAsk = true;
    const seen = f.orderSeen;
    void endpoints
      .balance()
      .then(
        value => {
          if (f.orderSeen === seen) rememberCircleBalance(value?.circle);
        },
        () => {
          /* ошибка — проверки нет, решает сервер (402), как у родной кнопки */
        },
      )
      .finally(() => {
        f.balanceAsk = false;
        if (cur() === f && onForm()) h.syncFoot();
      });
  }
  /** Что починить вместо создания: форма или образы не загрузились, форма устарела — перечитать; загрузка не подтверждена — продолжить */
  function recovery(f: Frame): ((btn: HTMLElement) => void) | null {
    const { s } = live(f);
    if ((!s.form && s.error && !s.integrityError) || s.versionStale) return () => f.svc.setters.revision(value => value + 1);
    if (s.form && f.doc.error()) return () => void f.doc.reload();
    if (f.media.stalled() && !s.busy && s.error === TRANSPORT) return btn => void busyButton(btn, () => f.media.resume());
    return null;
  }
  /** Словари «Съёмки» (один раз на кадр): пришли — блок показывается, если совпадают; выбор из входов */
  function camLoad(f: Frame) {
    if (f.camAsked) return;
    f.camAsked = true;
    void f.cam.load().then(() => {
      f.camDone = true;
      if (cur() !== f) return;
      if (f.cam.has() && f.entered) f.cam.restore(f.svc.get().inputs.shoot);
      if (h.mounted()) changed(f);
    });
  }

  /* ---- вход заказа: тело заявки (или подсмотренной), иначе форма ---- */
  const intentOf = (f: Frame | null): Intent | null => {
    const s = f?.svc.get();
    if (!f || !s) return null;
    return s.intent ?? (!s.identity && !s.integrityError ? f.peek : null);
  };
  const bodyInputs = (it: Intent) =>
    Object.fromEntries(Object.entries(it.body.inputs as Record<string, unknown>).map(([key, value]) => [key, typeof value === 'string' ? value : JSON.stringify(value)]));
  /** Образы заказа в виде снимка: тело заявки, иначе S */
  function sentLooks(f: Frame | null): { looks: ShootSnap['looks']; ar: string; text: string; people: Array<{ act: string }> } {
    const form = f?.svc.get().form ?? null;
    const it = intentOf(f);
    const L = f?.lists;
    const shape = (look: { id: string; n: string; shots: Array<{ n: string }> }) => ({ id: look.id, n: look.n, f: '', shots: look.shots.map(shot => ({ n: shot.n })) });
    if (f && form && L && it) {
      const inputs = bodyInputs(it);
      const v = readShoot(form, L, inputs);
      const looks = v.looks.map(id => L.looks.find(look => look.id === id)).filter((look): look is EngineLook => !!look).map(shape);
      if (v.ref) looks.push({ id: 'ref', n: L.ref.name || uiText('Свой референс'), f: '', shots: h.SHOT_NAMES.slice(0, L.ref.shots).map(n => ({ n })) });
      const persons = decode<unknown[]>(inputs.persons, []);
      const acts = (Array.isArray(persons) ? persons : []).map(item => ({ act: text(record(item).action) }));
      return { looks, ar: v.ratio, text: v.wishes, people: acts };
    }
    return {
      looks: h.curLooks().map(shape),
      ar: S.ar,
      text: S.text,
      people: S.people.filter(x => !isChar(x)).map(x => ({ act: x.act === 'own' ? (x.own ?? '') : (x.act ?? '') })),
    };
  }

  /* ---- хвост BotRun: фасад одного экземпляра на кадр ---- */
  function rebuild() {
    if (!tailOpts) return;
    const { create, opts } = tailOpts;
    const base = typeof opts.missing === 'function' ? (opts.missing as () => string) : () => '';
    const paint = typeof opts.paint === 'function' ? (opts.paint as (r: BotRunRun) => void) : null;
    const more = typeof opts.more === 'function' ? (opts.more as (r: BotRunRun) => string) : null;
    inst = create({
      ...opts,
      drive: (r: BotRunRun) => server.drive(r),
      failText: (r: BotRunRun) => server.failText(r),
      receipt: (r: BotRunRun) => server.receipt(r),
      /* подписи и тексты страницы заказа — на языке профиля в момент показа (BotRun берёт их через val: функции) */
      foot: chatFoot(serverFoot({ ...((opts.foot as Record<string, unknown>) ?? {}), mod: ['data-ss-again', 'edit', () => uiText('Изменить настройки')] }, 'ss'), 'ss'),
      T: {
        ...((opts.T as Record<string, unknown>) ?? {}),
        runH: () => uiText('Создаём фотосессию'),
        runP: () => uiText('Можно уйти с экрана: бот пришлёт фото в чат по мере готовности.'),
        doneH: () => uiText('Фотосессия готова'),
        doneP: (r: BotRunRun) => server.doneText(r),
        modH: () => uiText(FAIL.modH),
        techH: () => uiText(FAIL.techH),
        notMade: () => uiText(FAIL.notMade),
        modStep: () => uiText('Не прошло проверку'),
        sent: () => uiText('фото'),
      },
      /* смена страницы start прежнего хвоста (SteosUI.swap, 170 мс) может прийти после смены кадра: его RUN уже не хвост экрана — без тела */
      ...(more ? { more: (r: BotRunRun) => (inst && inst.run !== r ? '' : more(r)) } : {}),
      steps: () => server.steps(),
      snap: () => server.snap(),
      price: () => server.sentPrice(),
      /* ход кадров — подшаг сервера (progress.done, run-driver k); без него счётчика и полосы нет (решение Q-S3):
         пустая полоса на нуле выглядела лишней серой линией под текстом */
      paint: (r: BotRunRun) => {
        const run = r as BotRunRun & { done?: number };
        const k = typeof r.k === 'number' ? r.k : null;
        run.done = k === null ? 0 : Math.max(0, Math.min(k, Number(r.snap.n) || 0));
        paint?.(r);
        const n = h.root()?.querySelector<HTMLElement>('#ssPrgN');
        if (k === null && n) n.textContent = '';
        const bar = n?.parentElement;
        if (bar && k === null) bar.style.display = 'none';
        else if (bar?.style.display) {
          bar.style.removeProperty('display');
          if (!bar.getAttribute('style')) bar.removeAttribute('style');
        }
      },
      /* повтор заявки и «Попробовать ещё раз» решает сервис: форма в памяти им не нужна */
      missing: () => {
        const f = cur();
        if (f) {
          const { s, d } = live(f);
          if (s.intent || d.orderId) return '';
        }
        return base();
      },
    });
    for (const key of Object.keys(inst)) {
      if (key === 'run' || Object.hasOwn(facade, key)) continue;
      (facade as unknown as Record<string, unknown>)[key] = (...args: unknown[]) =>
        ((inst as unknown as Record<string, (...a: unknown[]) => unknown>)[key])(...args);
    }
  }
  Object.defineProperty(facade, 'run', { enumerable: true, get: () => inst?.run ?? null });

  /* ---- итог: кадры по секциям ---- */
  /** Раскладка итога: секции ответа (образы) с номерами файлов-картинок; без секций — по образам заказа, если число кадров сходится, иначе одной группой */
  function relayout(r: BotRunRun, f: Frame): boolean {
    const order = f.svc.get().order;
    const snap = r.snap as ShootSnap;
    const images = (order?.result?.assets ?? [])
      .filter(asset => RESULT_LIMITS.mimes!.test(asset.mime))
      .map(asset => asset.index)
      .sort((a, b) => a - b);
    if (!images.length) return false;
    const known = new Set(images);
    const secs = sections(order)
      .map(section => ({ n: section.title, idx: section.indices.filter(i => known.has(i)) }))
      .filter(section => section.idx.length);
    const looks = Array.isArray(snap.looks) ? snap.looks : [];
    let groups: Array<{ n: string; idx: number[]; names?: string[] }>;
    if (secs.length) groups = secs.map((section, i) => {
      const look = looks.find(item => item.n === section.n) ?? looks[i];
      return { n: section.n || look?.n || String(snap.title ?? ''), idx: section.idx, names: look?.shots.map(shot => shot.n) };
    });
    else if (looks.reduce((sum, look) => sum + look.shots.length, 0) === images.length) {
      let at = 0;
      groups = looks.map(look => ({ n: look.n, idx: images.slice(at, (at += look.shots.length)), names: look.shots.map(shot => shot.n) }));
    } else groups = [{ n: looks.length === 1 ? looks[0].n : String(snap.title ?? ''), idx: images, names: looks.length === 1 ? looks[0].shots.map(shot => shot.n) : undefined }];
    const next = groups.map((g, i) => ({ id: `r${i}`, n: g.n, f: '', shots: g.idx.map((_, j) => ({ n: g.names?.[j] || uiFormat('Кадр {0}', j + 1) })) }));
    const idx = groups.flatMap(g => g.idx);
    const sig = (x: { looks: unknown; idx: unknown }) => JSON.stringify([x.looks, x.idx]);
    if (sig({ looks: next, idx }) === sig({ looks: snap.looks, idx: snap.idx })) return false;
    /* oneLook шаблона: у экрана с выбором образов (conf.pick) итог всегда по образам — с подписью образа даже у одного */
    Object.assign(snap, { looks: next, idx, n: idx.length, one: !(f.lists?.conf.pick ?? false) && groups.length === 1 });
    return true;
  }
  /** Кадры, которые пришли, — в кнопки итога на месте (якоря не пересоздаются) */
  function paintResults() {
    h.root()
      ?.querySelectorAll<HTMLElement>('#ssRunB [data-ss-res]')
      .forEach(btn => {
        if (btn.querySelector('img')) return;
        const pic = server.resPic(Number(btn.dataset.ssRes));
        if (pic) btn.insertAdjacentHTML('afterbegin', pic);
      });
  }
  /** Заказ готов (каждая сверка): раскладка по секциям и кадры — один раз на заказ и ревизию */
  function onDone(f: Frame) {
    const order = f.svc.get().order;
    const r = h.tail.run;
    if (!order || order.status !== 'done' || !r || r.st !== 'done') return;
    const key = galleryKey(order);
    if (f.doneKey === key && f.doneRun === r) return;
    f.doneKey = key;
    f.doneRun = r;
    if (relayout(r, f)) h.tail.refresh();
    void f.gallery.load(order).then(items => {
      if (!items || h.tail.run !== r || cur() !== f) return;
      paintResults();
    });
  }
  /** Файл итога кадра k (null — ещё не пришёл) */
  function resultItem(f: Frame | null, k: number) {
    const order = f?.svc.get().order;
    const index = (h.tail.run?.snap as ShootSnap | undefined)?.idx?.[k];
    if (!f || !order || index === undefined) return null;
    return f.gallery.items(order).find(item => item.index === index) ?? null;
  }
  /** «Сделать рилс», «Полароид-рилс»: первый кадр итога — в «Рилс» после сверки владельца и заказа (решение Q-S5, как родной хвост) */
  function handoff(f: Frame, el: HTMLElement) {
    const s = f.svc.get();
    const order = s.order;
    const r = h.tail.run;
    const first = (r?.snap as ShootSnap | undefined)?.idx?.[0];
    if (!order || order.status !== 'done' || r?.st !== 'done' || first === undefined || f.moving) return;
    f.moving = true;
    const items = f.gallery.items(order);
    void busyButton(el, () =>
      handoffPhoto({ ownerId: s.identity?.owner, order, index: first, destination: 'reels', blobs: index => items.find(item => item.index === index)?.blob }),
    )
      .catch(() => {
        if (el.isConnected) tip(el, 'Не удалось передать кадр. Откройте работу в «Истории» и попробуйте снова.');
      })
      .finally(() => {
        f.moving = false;
      });
  }
  /** «Назад» внутри экрана (Telegram, «назад» раздела): сначала верхний слой — «Улучшить», поле во весь экран, лист экрана, людей или ядра */
  function back(f: Frame): boolean {
    if (cur() !== f || !h.mounted()) return false;
    const bc = window.BotCore as unknown as { improve?: { isOpen: boolean; close(): void }; editor?: { isOpen: boolean; close(): void } };
    if (bc.improve?.isOpen) {
      bc.improve.close();
      return true;
    }
    if (bc.editor?.isOpen) {
      bc.editor.close();
      return true;
    }
    if (S.sh) {
      h.sheetClose();
      return true;
    }
    if (h.people.isOpen) {
      h.people.close();
      return true;
    }
    const sheets = [...document.querySelectorAll<HTMLElement>('.bsheet.open[aria-hidden="false"]')];
    const top = sheets.at(-1);
    const x = top?.querySelector<HTMLElement>('[data-bsheet-x],[data-ss-shx],.xbtn');
    if (!x) return false;
    x.click();
    return true;
  }

  /* ---- привязка кадра ---- */
  let watching = false;
  /** «Вы» и близкие сменились (server-state, листы людей, другой экран): ряд лиц на месте, мозаика, кнопка и входы — заново */
  function watch() {
    if (watching) return;
    watching = true;
    watchPeople({
      paint: () => h.people.paintForm(),
      mounted: () => !!cur() && onForm(),
      changed: () => {
        h.moSync();
        h.syncFoot();
      },
      m: safePeople,
    });
  }
  function dropFrame(f: Frame) {
    f.media.cancel();
    f.ref.cancel();
    f.gallery.release();
    f.doc.abort();
  }
  function bind(b: EngineBinding): EngineBound {
    watch();
    if (!frame || frame.key !== b.key || frame.svc !== b.svc) {
      if (frame) dropFrame(frame);
      frame = makeFrame(b);
    } else frame.b = b;
    const mine = frame;
    bound = mine;
    return {
      changed: () => changed(mine),
      release() {
        if (bound === mine) bound = null;
      },
      back: () => back(mine),
    };
  }
  function makeFrame(b: EngineBinding): Frame {
    const f = {} as Frame;
    const guard =
      <A extends unknown[]>(fn: (...args: A) => void) =>
      (...args: A) => {
        if (frame === f) fn(...args);
      };
    Object.assign(f, {
      b,
      key: b.key,
      svc: b.svc,
      app: b.app,
      mirror: createInputsMirror(b.svc),
      form: null,
      data: undefined,
      lists: null,
      entered: false,
      layout: '',
      refRestoring: false,
      camAsked: false,
      camDone: false,
      said: '',
      peek: null,
      formShown: false,
      balanceAsk: false,
      orderSeen: undefined,
      doneKey: '',
      doneRun: null,
      moving: false,
    });
    f.doc = screenDoc<Record<string, unknown>>('shoot', { remember: true, onChange: guard(() => changed(f)) });
    f.media = createMediaBridge({
      svc: b.svc,
      owner: () => f.svc.get().identity?.owner,
      say: guard((text: string) => h.ref.say(text)),
      policy: key => mediaPolicy(f.svc.get().form, key),
      update: guard(() => {
        if (cur() === f && onForm()) h.syncFoot();
      }),
    });
    f.ref = createRefBridge({ media: f.media, svc: b.svc, owner: () => f.svc.get().identity?.owner });
    f.cam = createCamBridge({ block: () => h.cam, form: () => f.svc.get().form, inputs: () => f.svc.get().inputs });
    f.gallery = createResultGallery(RESULT_LIMITS);
    f.driver = createRunDriver({
      svc: b.svc,
      tail: () => h.tail,
      root: () => h.root(),
      mounted: () => cur() === f && h.mounted(),
      localSteps: () => server.steps(),
      paintForm: () => h.paintPage(),
      tip: (el, text) => tip(el, text),
      onDone: () => onDone(f),
      stepAlias: STEP_ALIAS,
      progress: order => tailProgress(order)?.done,
    });
    return f;
  }

  const refOpts: RefOpts = {
    upload: (file, e) => {
      const f = cur();
      return f ? f.ref.opts().upload(file, e) : Promise.resolve({});
    },
    remove: e => frame?.ref.opts().remove(e),
  };

  const server: ShootServer = {
    ui: base =>
      serverUi(base, {
        poorBalance: () => {
          const f = cur();
          /* кнопка чинит («Обновить форму», «Продолжить загрузку»), а не создаёт: «Не хватает · Пополнить» её не заслоняет */
          if (!f || recovery(f)) return Infinity;
          const s = f.svc.get();
          return poorBalance({ balance: circleBalance(), form: s.form, intent: s.intent }, true);
        },
      }),
    open() {
      const f = cur();
      if (!f) {
        /* без привязки (кадр не активен) — форма без действий: missing() держит «Проверяем доступность» */
        h.paintPage();
        return;
      }
      if (painted !== f) {
        /* другой кадр или новый сервис под прежним ключом: образы, люди, референс, «Съёмка», скидка и хвост прежнего сюда не переходят */
        h.lbClose(true);
        h.sheetClose();
        h.people.close();
        h.cam.reset();
        h.ref.restore(null);
        Object.assign(S, { key: null, shoot: null, app: null, looks: [], people: [], extra: [], ar: '3:4', text: '', open: false, cheap: false, menu: null, sh: null });
        rebuild();
        painted = f;
        f.layout = '';
        f.form = null;
        f.data = undefined;
        f.entered = false;
      }
      f.formShown = false;
      const root = h.root();
      if (h.tail.run && root) {
        h.tail.show(root);
        changed(f);
        return;
      }
      f.peek = peekIntent(f.app.id);
      void f.doc.load();
      camLoad(f);
      h.people.prune();
      /* входы → S до первой отрисовки: подвал формы пишет S обратно во входы */
      syncState(f);
      if (f.driver.first(f.peek) === 'form' && root) root.scrollTop = 0;
      changed(f);
    },
    close() {
      /* лист согласия и чтения превью — у мостов кадра: уход их не трогает (заказ и загрузки ведёт сервис) */
    },
    tail(create, opts) {
      tailOpts = { create, opts };
      rebuild();
      return facade;
    },
    wrap<T>(_slot: 'cheap', value: T): T {
      const cheap = value as unknown as { price?: unknown; go?: unknown; where?: unknown };
      const offer = () => {
        const f = cur();
        return f ? studioCheapOffer(f.svc.get().form) : null;
      };
      /* скидку «Сделать дешевле» публикует форма (Q5): без неё у подвала нет ни цены, ни действия скидки */
      return {
        get where() {
          return typeof cheap.where === 'string' ? uiText(cheap.where) : cheap.where;
        },
        get price() {
          return offer() ? () => server.price(true) : undefined;
        },
        get go() {
          return offer() ? cheap.go : undefined;
        },
        get percent() {
          return offer()?.percent;
        },
      } as unknown as T;
    },
    has(k) {
      const f = cur() ?? frame;
      const form = f?.svc.get().form ?? null;
      const L = f?.lists;
      if (!f || !form || !L) return false;
      const keys = shootKeys(form);
      switch (k) {
        case 'chars':
          return !!keys.chars;
        case 'ratio':
          return !!keys.ratio && L.formats.length > 0;
        case 'shoot':
          return !!keys.shoot && L.kind !== 'app' && f.cam.has();
        default:
          return !!keys.wishes;
      }
    },
    conf: () => (cur() ?? frame)?.lists?.conf ?? NEUTRAL(),
    refOn() {
      const f = cur() ?? frame;
      return !!f?.lists?.conf.pick && f.ref.has();
    },
    refOpts: () => refOpts,
    lookImg: look => look.url || '',
    meUrl: () => selfPicture(safePeople()),
    unit() {
      const f = cur() ?? frame;
      const form = f?.svc.get().form;
      const L = f?.lists;
      if (!form || !L || L.unit === null || form.chargeable === false) return '';
      return core().html.unit(L.unit, uiText(L.unitWord));
    },
    badge: look => (look.isNew ? uiText('Новое') : look.hit ? uiText('Хит') : ''),
    skinItems,
    improveKind() {
      const f = cur() ?? frame;
      const form = f?.svc.get().form;
      if (!form || !shootKeys(form).wishes) return '';
      const kind = record(record(form.constraints).shoot).improvement_kind;
      return isImproveKind(kind) ? kind : '';
    },
    price(cheap) {
      const f = cur() ?? frame;
      const s = f?.svc.get();
      const form = s?.form;
      if (!f || !s || !form) return toolPrice(f?.app.price).amount ?? 0;
      if (cheap) {
        const offer = studioCheapOffer(form);
        if (offer) return offer.price;
      }
      /* расчёт сервера под нынешний состав — цена кнопки; до него — оценка по цене кадра прайса */
      const d = f.svc.derive();
      const unit = f.lists?.unit;
      if (form.chargeable === false || d.quoted || unit === null || unit === undefined) return d.price;
      return unit * h.nShots();
    },
    missing() {
      const f = cur();
      if (!f) return 'load';
      const { s, d } = live(f);
      if (s.intent && !d.orderId) return '';
      if (!s.identity && !s.integrityError) return 'load';
      if (s.integrityError || !s.identity || s.identity.appId !== f.app.id) return 'blocked';
      /* форма не загрузилась или устарела: кнопка «Обновить форму» (recovery) */
      if (!s.form) return s.error ? '' : 'load';
      if (s.versionStale) return '';
      if (!shootSupported(s.form)) return 'off';
      if (f.doc.error()) return '';
      if (!f.lists || !f.entered) return 'docs';
      if (!s.form.available) return 'unavailable';
      return null;
    },
    ready() {
      const f = cur();
      if (!f) return uiText('Проверяем доступность');
      const { s, d } = live(f);
      const form = s.form;
      if (!form || !f.lists) return uiText('Проверяем доступность');
      /* неподтверждённая загрузка референса: «Продолжить загрузку» (recovery) — кнопка активна */
      if (f.media.pending()) return f.media.stalled() && s.error === TRANSPORT && !s.busy ? '' : uiText('Проверяем картинку');
      if (f.refRestoring) return uiText('Проверяем картинку');
      for (const x of S.people) {
        if (isChar(x)) continue;
        const ref = personRef(x.id, safePeople());
        if (!ref.ok) return ref.text;
      }
      if (S.extra.length) return uiText('Этот человек не участвует');
      const keys = shootKeys(form);
      const handled = [...Object.values(keys).filter((key): key is string => !!key), ...f.ref.keys()];
      const gap = requiredGap(form, s.inputs, handled);
      if (gap) return uiText(gap);
      if (form.chargeable !== false && server.price(false) !== d.price) return uiText('Цена этого выбора пока не подтверждена');
      return '';
    },
    missText(kind) {
      const f = cur();
      const s = f?.svc.get();
      switch (kind) {
        case 'load':
          return uiText('Проверяем доступность');
        case 'blocked':
          return uiText(s?.integrityError || 'Проверяем доступность');
        case 'off':
          return uiText(s?.error || 'Этот инструмент сейчас недоступен. Попробуйте обновить форму.');
        case 'unavailable':
          return uiText('Создание сейчас недоступно');
        case 'docs':
          return uiText('Загружаем образы…');
        default:
          return '';
      }
    },
    label() {
      const f = cur();
      if (!f) return null;
      const { s, d } = live(f);
      if (s.intent && !d.orderId) return uiText('Продолжить проверку');
      if ((!s.form && s.error && !s.integrityError) || s.versionStale || (s.form && f.doc.error())) return uiText('Обновить форму');
      if (f.media.stalled() && !s.busy && s.error === TRANSPORT) return uiText('Продолжить загрузку');
      if (s.form?.chargeable === false) {
        const n = h.nShots();
        return n ? uiFormat('Создать {0}', shotsW(n)) : uiText('Создать фотосессию');
      }
      return null;
    },
    write() {
      const f = cur();
      if (!f || !onForm()) return;
      commit(f);
    },
    start(btn) {
      const f = cur();
      if (!f) return;
      const { s, d } = live(f);
      if (s.intent && !d.orderId) {
        h.tail.start();
        return;
      }
      const fix = recovery(f);
      if (fix) {
        fix(btn);
        return;
      }
      if (h.foot.poor()) {
        h.foot.poorOpen();
        return;
      }
      h.tail.start();
    },
    steps: () => STEPS.map(([key, icon, run, done]) => [key, icon, uiText(run), uiText(done)]),
    snap() {
      const f = cur() ?? frame;
      const it = intentOf(f);
      const sent = sentLooks(f);
      const n = sent.looks.reduce((sum, look) => sum + look.shots.length, 0);
      const me = meIn() ? selfRef(safePeople()) : null;
      return {
        title: h.title(),
        one: !(f?.lists?.conf.pick ?? false) && sent.looks.length === 1,
        looks: sent.looks,
        me: { src: me?.src ?? null, url: me ? selfPicture(safePeople()) : '', av: null, name: uiText('Вы') },
        people: sent.people,
        chars: [],
        ar: sent.ar,
        cheap: !!(it ? it.body.cheap : S.cheap),
        text: sent.text.trim(),
        n,
        idx: [],
      };
    },
    sentPrice: () => cur()?.svc.derive().price ?? 0,
    drive(r) {
      const f = cur();
      if (f) void f.driver.drive(r);
    },
    failText(r) {
      const f = cur() ?? frame;
      const s = f?.svc.get();
      const order = s?.order ?? null;
      /* у шаблона сбой без «на шаге …» (T.techStep:false хвоста экрана): этап сбоя не называется */
      return failText(r, { order, error: s ? s.integrityError || s.error : '', texts: FAIL });
    },
    receipt(r) {
      const f = cur() ?? frame;
      const s = f?.svc.get();
      const cheap = s?.intent ? !!s.intent.body.cheap : !!r.snap.cheap;
      return receipt(r, s?.order ?? null, { cheap, where: 'на фото' });
    },
    doneText(r) {
      const f = cur() ?? frame;
      const order = f?.svc.get().order ?? null;
      const snap = r.snap as ShootSnap;
      const n = Number(snap.n) || 0;
      const groups = Array.isArray(snap.looks) ? snap.looks.length : 0;
      /* doneP шаблона: sn.one — только кадры, иначе и образы («3 кадра в 1 образе» у экрана с выбором образов) */
      const what = snap.one || groups < 1 ? shotsW(n) : uiFormat('{0} в {1}', shotsW(n), plural(groups, uiText('образе'), uiText('образах'), uiText('образах')));
      /* родной хвост: бот уже прислал (доставка sent, delivered) или пришлёт; не присылает — текст доставки (tail-done) */
      const sent = ['sent', 'delivered'].includes(String(record(record(order).delivery).state));
      const base = esc(uiFormat(sent ? '{0}. Бот уже прислал их в чат, в отдельную тему.' : '{0}. Бот пришлёт их в чат, в отдельную тему.', what));
      return doneP(order, base, 'ss');
    },
    resPic(k) {
      const item = resultItem(cur(), k);
      return item ? `<img src="${esc(item.url)}" alt="" draggable="false">` : '';
    },
    saveAll(btn) {
      const f = cur();
      const order = f?.svc.get().order;
      if (!f || order?.status !== 'done') return;
      const items = f.gallery.items(order);
      const bc = core();
      if (!items.length || !bc.saveBtn) {
        tip(btn, 'Кадры ещё загружаются');
        return;
      }
      const ui = h.ui();
      /* архив итога (owner-protected путь заказа), иначе один кадр — сам файл, несколько — zip кадров */
      bc.saveBtn(
        btn,
        async () => {
          if (archiveUrl(order)) return { data: await archive(order), name: 'steos-photoshoot.zip' };
          if (items.length === 1) return { data: items[0].blob, name: `steos-photoshoot${extOf(items[0].mime)}` };
          if (!bc.zip) throw new Error('zip_unavailable');
          return { data: await bc.zip(items.map((item, k) => ({ name: `photo-${pad(k + 1)}${extOf(item.mime)}`, data: item.blob }))), name: 'steos-photoshoot.zip' };
        },
        { buzz: () => ui.buzz(), tip: (el: Element, text: string) => ui.tip(el, text) },
      );
    },
    save(btn, k) {
      const item = resultItem(cur(), k);
      const bc = core();
      if (!item || !bc.saveBtn) {
        tip(btn, 'Кадры ещё загружаются');
        return;
      }
      const ui = h.ui();
      bc.saveBtn(btn, async () => ({ data: item.blob, name: `steos-photoshoot-${pad(k + 1)}${extOf(item.mime)}` }), {
        round: true,
        buzz: () => ui.buzz(),
        tip: (el: Element, text: string) => ui.tip(el, text),
      });
    },
    leave(go) {
      const f = cur();
      if (!f) return;
      void f.driver.leave(go, false, '');
    },
    click(d, el) {
      const f = cur();
      const s = f?.svc.get();
      const r = h.tail.run;
      if (d.ssChat !== undefined) {
        if (r?.st === 'done') chatClick({ order: s?.order, el, text: 'Тут откроется тема чата, куда бот прислал фото', tip: (at, text) => h.ui().tip(at, text) });
        return true;
      }
      if (d.ssSettings !== undefined) {
        openSettings();
        return true;
      }
      if (d.ssReels !== undefined || d.ssSlides !== undefined) {
        if (f) handoff(f, el);
        return true;
      }
      if (d.ssRetry === undefined && d.ssRchk === undefined && d.ssFix === undefined) return false;
      if (!f || f.driver.busy()) return true;
      if (d.ssRetry !== undefined) h.tail.retry();
      else if (d.ssRchk !== undefined) void f.driver.check(el);
      else void f.driver.leave(() => h.tail.exit(() => h.paintPage()), false, '');
      return true;
    },
  };

  /* ---- смена языка профиля (i18n: событие steos:locale) ---- */
  /** Подписи этапов идущего заказа — строки на момент создания хвоста: им — подписи нового языка (ключи этапов те же) */
  function relabel(r: BotRunRun) {
    const fresh = server.steps();
    for (const step of r.steps) {
      const same = fresh.find(item => item[0] === step[0]);
      if (same) {
        step[2] = same[2];
        step[3] = same[3];
      }
    }
  }
  /** Открытый экран рисуется заново на новом языке: списки шаблона (форматы, метки образов), хвост — TAIL.show, форма — paintPage */
  function relocale() {
    const r = h.tail.run;
    if (r) relabel(r);
    const f = cur();
    const root = h.root();
    if (!f || !root || !h.mounted()) return;
    if (f.lists) fillLists(f);
    if (r) h.tail.show(root);
    else h.paintPage();
    changed(f);
  }
  document.addEventListener('steos:locale', relocale);
  /* новый снимок Me уже положил остаток в память кнопки (tool-helpers): подвал формы — по нему */
  document.addEventListener('steos:account-updated', () => {
    if (cur() && onForm()) h.syncFoot();
  });

  instance = { bind };
  return server;
}

/** ENGINES.shoot.bind: привязка кадра к серверу экрана, созданному движком (без движка в серверном режиме — пустая привязка) */
export function bindShootEngine(b: EngineBinding): EngineBound {
  return instance ? instance.bind(b) : { changed() {}, release() {} };
}

/** Экран «shoot» реестра движков студии: движок шаблона window.BotShoot, атрибуты data-ss-*, без правой панели, знак заказа «shoot» (Q-S8) */
export const SHOOT_ENGINE: EngineScreen = { global: 'BotShoot', prefix: 'ss', side: false, kind: 'shoot', bind: bindShootEngine };

export function registerShootEngine() {
  ENGINES.shoot = SHOOT_ENGINE;
}
